import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';

/**
 * Searchable single-select dropdown with ArrowUp/ArrowDown/Enter support.
 * Options are expected to have `Id` and `Name` or `Number`.
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  required = false,
  label,
  searchPlaceholder = 'Search...',
  getOptionLabel = (opt) => opt?.Name || opt?.Number || '',
  getOptionValue = (opt) => opt?.Id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => getOptionValue(opt) === value);
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : '';

  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return options;
    return options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(term));
  }, [options, searchTerm, getOptionLabel]);

  const handleSelect = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
  };

  const { highlightedIndex, setHighlightedIndex, onKeyDown, setItemRef } = useListKeyboardNav({
    isOpen,
    itemCount: filteredOptions.length,
    onSelectIndex: (index) => {
      const option = filteredOptions[index];
      if (option) handleSelect(option);
    },
    onClose: () => {
      setIsOpen(false);
      setSearchTerm('');
    },
    resetKey: searchTerm,
  });

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent text-left flex items-center justify-between ${
            disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
        >
          <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={onKeyDown}
                  className="w-full pl-8 pr-4 py-2 border-0 border-b-2 border-gray-200 focus:outline-none focus:border-[#46955A] bg-transparent text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto" role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option);
                  const isHighlighted = index === highlightedIndex;
                  const isSelected = value === optionValue;
                  return (
                    <button
                      key={optionValue ?? index}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      ref={(node) => setItemRef(index, node)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(option)}
                      className={`w-full px-4 py-2 text-left transition-colors ${
                        isHighlighted || isSelected
                          ? 'bg-[#f0f9f4] text-[#46955A] font-medium'
                          : 'text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {getOptionLabel(option)}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">No options found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableSelect;
